import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, FileText, Plus, Minus, ArrowRight, Loader2, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keeping original data structure for reference - will be replaced by API data
const COMPARATIVE_DATA_BACKUP = [
  {
    article: 'I',
    title9184: 'General Provisions',
    title12009: 'General Provisions',
    sections: [
      { sec9184: '1', title9184: 'Short Title', sec12009: '1', title12009: 'Short Title', status: 'retained' },
      { sec9184: '2', title9184: 'Declaration of Policy', sec12009: '2', title12009: 'Declaration of Policy', status: 'retained' },
      { sec9184: '3', title9184: 'Governing Principles on Government Procurement', sec12009: '3', title12009: 'Governing Principles on Government Procurement', status: 'retained' },
      { sec9184: '4', title9184: 'Scope and Application', sec12009: '4', title12009: 'Scope and Application', status: 'retained' },
      { sec9184: '5', title9184: 'Definition of Terms', sec12009: '5', title12009: 'Definition of Terms', status: 'retained' },
      { sec9184: '6', title9184: 'Standardization of Procurement Process and Forms', sec12009: '6', title12009: 'Standardization of Procurement Process and Forms', status: 'retained' }
    ]
  },
  {
    article: 'II',
    title9184: 'Procurement Planning',
    title12009: 'Strategic Procurement Planning',
    sections: [
      { sec9184: '7', title9184: 'Procurement Planning and Budgeting Linkage', sec12009: '7', title12009: 'Strategic Procurement Planning and Budgeting Linkage', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '8', title12009: 'Detailed Engineering for the Procurement of Infrastructure Projects', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '9', title12009: 'Multi-Year Contracting', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '10', title12009: 'Market Scoping', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '11', title12009: 'Specifications', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '12', title12009: 'Early Procurement Activities', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '13', title12009: 'Lifecycle Assessment and Lifecycle Cost Analysis', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '14', title12009: 'Design-and-Build Scheme for Infrastructure Projects', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '15', title12009: 'Engagement of a Procurement Agent', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '16', title12009: 'Use of Framework Agreement', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '17', title12009: 'Pooled Procurement', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '18', title12009: 'Renewal of Regular and Recurring Services', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '19', title12009: 'Warehousing and Inventory System', status: 'added' }
    ]
  },
  {
    article: 'III',
    title9184: 'Procurement by Electronic Means',
    title12009: 'Procurement by Electronic Means',
    sections: [
      { sec9184: '8', title9184: 'Procurement By Electronic Means', sec12009: '20', title12009: 'Procurement by Electronic Means', status: 'modified' },
      { sec9184: '9', title9184: 'Security, Integrity and Confidentiality', sec12009: '22', title12009: 'Security, Integrity and Confidentiality', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '21', title12009: 'Electronic Payment', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '23', title12009: 'Systems Interconnectivity between Government Agencies', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '24', title12009: 'Procurement Data Analytics', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '25', title12009: 'Maintenance, Update, and Backup', status: 'added' }
    ]
  },
  {
    article: 'IV',
    title9184: 'Competitive Bidding',
    title12009: 'Modes of Procurement',
    sections: [
      { sec9184: '10', title9184: 'Competitive Bidding', sec12009: '26', title12009: 'Modes of Procurement', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '27', title12009: 'Competitive Bidding', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '28', title12009: 'Limited Source Bidding', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '29', title12009: 'Competitive Dialogue', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '30', title12009: 'Unsolicited Offer with Bid Matching', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '31', title12009: 'Direct Contracting', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '32', title12009: 'Direct Acquisition', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '33', title12009: 'Repeat Order', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '34', title12009: 'Small Value Procurement', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '35', title12009: 'Negotiated Procurement', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '36', title12009: 'Direct Sales', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '37', title12009: 'Direct Procurement for Science, Technology and Innovation', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '38', title12009: 'Video Recording of All Procurement-Related Conferences', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '39', title12009: 'Prohibition on Splitting of Government Contracts', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '40', title12009: 'Procurement of CSE', status: 'added' }
    ]
  },
  {
    article: 'V',
    title9184: 'Bids and Awards Committee',
    title12009: 'Bids and Awards Committee',
    sections: [
      { sec9184: '11', title9184: 'The BAC and its Composition', sec12009: '41', title12009: 'The BAC and its Composition', status: 'retained' },
      { sec9184: '12', title9184: 'Functions of the BAC', sec12009: '42', title12009: 'Functions of the BAC', status: 'retained' },
      { sec9184: '13', title9184: 'Observers', sec12009: '43', title12009: 'Observers', status: 'retained' },
      { sec9184: '14', title9184: 'BAC Secretariat', sec12009: '44', title12009: 'Procurement Unit', status: 'modified' },
      { sec9184: '15', title9184: 'Honoraria of BAC Members', sec12009: '45', title12009: 'Honoraria', status: 'retained' },
      { sec9184: '16', title9184: 'Professionalization of BAC, BAC Secretariat and TWG Members', sec12009: '46', title12009: 'Professionalization of Government Procurement Practitioners', status: 'modified' }
    ]
  },
  {
    article: 'VI',
    title9184: 'Preparation of Bidding Documents',
    title12009: 'Preparation of Bidding Documents',
    sections: [
      { sec9184: '17', title9184: 'Form and Contents of Bidding Documents', sec12009: '47', title12009: 'Form and Content of Bidding Documents', status: 'retained' },
      { sec9184: '18', title9184: 'Reference to Brand Names', sec12009: '-', title12009: 'Transposed to Section 11 re Specifications', status: 'transposed' }
    ]
  },
  {
    article: 'VII',
    title9184: 'Invitation to Bid',
    title12009: 'Invitation to Bid',
    sections: [
      { sec9184: '19', title9184: 'Access to Information', sec12009: '48', title12009: 'Access to Information', status: 'retained' },
      { sec9184: '20', title9184: 'Pre-Procurement Conference', sec12009: '49', title12009: 'Pre-Procurement Conference', status: 'retained' },
      { sec9184: '21', title9184: 'Advertising and Contents of the Invitation to Bid', sec12009: '50', title12009: 'Publication and Contents of the Invitation to Bid', status: 'modified' },
      { sec9184: '22', title9184: 'Pre-bid Conference', sec12009: '51', title12009: 'Pre-bid Conference', status: 'retained' },
      { sec9184: '23', title9184: 'Eligibility Requirements for Procurement of Goods and Infrastructure Projects', sec12009: '52', title12009: 'Eligibility Requirements for Procurement of Goods, Infrastructure Projects, and Consulting Services', status: 'modified' },
      { sec9184: '24', title9184: 'Eligibility Requirements and Short Listing for Consulting Services', sec12009: '53', title12009: 'Short-Listing for Consulting Services', status: 'modified' },
      { sec9184: '25', title9184: 'Submission and Receipt of Bids', sec12009: '54', title12009: 'Submission and Receipt of Bids', status: 'retained' },
      { sec9184: '26', title9184: 'Modification and Withdrawal of Bids', sec12009: '55', title12009: 'Modification and Withdrawal of Bids', status: 'retained' },
      { sec9184: '27', title9184: 'Bid Security', sec12009: '56', title12009: 'Bid Security', status: 'retained' },
      { sec9184: '28', title9184: 'Bid Validity', sec12009: '57', title12009: 'Bid Validity', status: 'retained' },
      { sec9184: '29', title9184: 'Bid Opening', sec12009: '58', title12009: 'Bid Opening', status: 'retained' },
      { sec9184: '30', title9184: 'Preliminary Examination of Bids', sec12009: '59', title12009: 'Preliminary Examination of Bids', status: 'retained' }
    ]
  },
  {
    article: 'IX',
    title9184: 'Bid Evaluation',
    title12009: 'Bid Evaluation',
    sections: [
      { sec9184: '31', title9184: 'Ceiling for Bid Prices', sec12009: '60', title12009: 'Ceiling for Bid Prices', status: 'retained' },
      { sec9184: '32', title9184: 'Bid for the Procurement of Goods and Infrastructure Projects', sec12009: '61', title12009: 'Bid Evaluation for Procurement of Goods and Infrastructure Projects', status: 'modified' },
      { sec9184: '33', title9184: 'Bid Evaluation of Short Listed Bidders for Consulting Services', sec12009: '62', title12009: 'Bid Evaluation of Short Listed Bidders for Consulting Services', status: 'retained' }
    ]
  },
  {
    article: 'X',
    title9184: 'Post-Qualification',
    title12009: 'Post-Qualification',
    sections: [
      { sec9184: '34', title9184: 'Objective and Process of Post-qualification', sec12009: '63', title12009: 'Objective and Process of Post-qualification', status: 'retained' },
      { sec9184: '35', title9184: 'Failure of Bidding', sec12009: '64', title12009: 'Failure of Bidding', status: 'retained' },
      { sec9184: '36', title9184: 'Single Calculated/Rated and Responsive Bid Submission', sec12009: '65', title12009: 'Single Calculated/Rated/Economically Advantageous and Responsive Bid Submission', status: 'modified' }
    ]
  },
  {
    article: 'XI',
    title9184: 'Award, Implementation and Termination of the Contract',
    title12009: 'Award, Implementation and Termination of the Contract',
    sections: [
      { sec9184: '37', title9184: 'Notice and Execution of Award', sec12009: '66', title12009: 'Notice and Execution of Award', status: 'retained' },
      { sec9184: '38', title9184: 'Period of Action on Procurement Activities', sec12009: '67', title12009: 'Period of Action on Procurement Activities', status: 'retained' },
      { sec9184: '39', title9184: 'Performance Security', sec12009: '68', title12009: 'Performance Security', status: 'retained' },
      { sec9184: '40', title9184: 'Failure to Enter into Contract and Post Performance Security', sec12009: '69', title12009: 'Failure to Enter into Contract and Post Performance Security', status: 'retained' },
      { sec9184: '41', title9184: 'Reservation Clause', sec12009: '70', title12009: 'Reservation Clause', status: 'retained' },
      { sec9184: '42', title9184: 'Contract Implementation and Termination', sec12009: '71', title12009: 'Contract Implementation and Termination', status: 'retained' }
    ]
  },
  {
    article: 'XII',
    title9184: 'Domestic and Foreign Procurement',
    title12009: 'Sustainable and Inclusive Procurement',
    sections: [
      { sec9184: '43', title9184: 'Procurement of Domestic and Foreign Goods', sec12009: '78', title12009: 'Procurement of Domestic and Foreign Goods', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '72', title12009: 'Sustainable Public Procurement', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '73', title12009: 'Green Public Procurement Strategy', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '74', title12009: 'Development of a Green Local Market', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '75', title12009: 'Inclusive Procurement Program', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '76', title12009: 'Registration of Certain Sectors', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '77', title12009: 'Exemptions and Other Forms of Support', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '79', title12009: 'Domestic Preference', status: 'added' }
    ]
  },
  {
    article: 'XIII',
    title9184: 'Bidding of Provincial Projects',
    title12009: '[DELETED]',
    sections: [
      { sec9184: '44', title9184: 'Bidding of Provincial Projects', sec12009: '-', title12009: '[DELETED]', status: 'deleted' },
      { sec9184: '45', title9184: 'Provincial Bidders', sec12009: '-', title12009: '[DELETED]', status: 'deleted' }
    ]
  },
  {
    article: 'XIV',
    title9184: 'Lease of Computers, Communications, Information and Other Equipment',
    title12009: 'Lease of Movable Properties',
    sections: [
      { sec9184: '46', title9184: 'Lease Contracts', sec12009: '80', title12009: 'Lease Contracts', status: 'modified' }
    ]
  },
  {
    article: 'XV',
    title9184: 'Disclosure of Relations',
    title12009: 'Disclosure of Relations',
    sections: [
      { sec9184: '47', title9184: 'Disclosure of Relations', sec12009: '81', title12009: 'Disclosure of Relations', status: 'retained' },
      { sec9184: '-', title9184: '-', sec12009: '82', title12009: 'Declaration of Beneficial Ownership', status: 'added' }
    ]
  },
  {
    article: 'XVI',
    title9184: 'Alternative Methods of Procurement',
    title12009: '[TRANSPOSED]',
    sections: [
      { sec9184: '48', title9184: 'Alternative Methods', sec12009: '-', title12009: 'Transposed under Section 26', status: 'transposed' },
      { sec9184: '49', title9184: 'Limited Source Bidding', sec12009: '-', title12009: 'Transposed to Section 28', status: 'transposed' },
      { sec9184: '50', title9184: 'Direct Contracting', sec12009: '-', title12009: 'Transposed to Section 31', status: 'transposed' },
      { sec9184: '51', title9184: 'Repeat Order', sec12009: '-', title12009: 'Transposed to Section 33', status: 'transposed' },
      { sec9184: '52', title9184: 'Shopping', sec12009: '-', title12009: '[DELETED]', status: 'deleted' },
      { sec9184: '53', title9184: 'Negotiated Procurement', sec12009: '-', title12009: 'Transposed to Section 35', status: 'transposed' },
      { sec9184: '54', title9184: 'Terms and Conditions for the use of Alternative Methods', sec12009: '-', title12009: '[DELETED]', status: 'deleted' }
    ]
  },
  {
    article: 'XVII',
    title9184: 'Protest Mechanism',
    title12009: 'Protest Mechanism',
    sections: [
      { sec9184: '55', title9184: 'Protests on Decisions of the BAC', sec12009: '83', title12009: 'Protests on Decisions of the BAC', status: 'modified' },
      { sec9184: '56', title9184: 'Resolution of Protests', sec12009: '84', title12009: 'Resolution of Protests', status: 'retained' },
      { sec9184: '57', title9184: 'Non-interruption of the Bidding Process', sec12009: '86', title12009: 'Non-interruption of the Bidding Process', status: 'modified' },
      { sec9184: '58', title9184: 'Report to Regular Courts; Certiorari', sec12009: '85', title12009: 'Resort to Regular Courts', status: 'modified' }
    ]
  },
  {
    article: 'XVIII',
    title9184: 'Settlement of Disputes',
    title12009: 'Settlement of Disputes',
    sections: [
      { sec9184: '59', title9184: 'Arbitration', sec12009: '88', title12009: 'Dispute Resolution', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '87', title12009: 'Prohibition on the Issuance of Temporary Restraining Orders', status: 'added' }
    ]
  },
  {
    article: 'XIX',
    title9184: 'Contract Prices and Warranties',
    title12009: 'Contract Prices and Warranties',
    sections: [
      { sec9184: '60', title9184: 'Appeals', sec12009: '-', title12009: 'Combined with Sec. 85', status: 'restructured' },
      { sec9184: '61', title9184: 'Contract Prices', sec12009: '89', title12009: 'Contract Prices', status: 'modified' }
    ]
  },
  {
    article: 'XX',
    title9184: 'The Government Procurement Policy Board',
    title12009: 'The Government Procurement Policy Board',
    sections: [
      { sec9184: '62', title9184: 'Warranty', sec12009: '90', title12009: 'Warranty', status: 'retained' },
      { sec9184: '63', title9184: 'Organization and Functions', sec12009: '91', title12009: 'Organization and Functions', status: 'retained' },
      { sec9184: '64', title9184: 'Membership', sec12009: '-', title12009: 'Combined with Sec. 91', status: 'restructured' }
    ]
  },
  {
    article: 'XXI',
    title9184: 'Penal Clause',
    title12009: '[RESTRUCTURED]',
    sections: [
      { sec9184: '65', title9184: 'Offenses and Penalties', sec12009: '-', title12009: 'Converted to Admin. Liability under Sec. 92', status: 'restructured' }
    ]
  },
  {
    article: 'XXII',
    title9184: 'Civil Liability / Administrative Sanctions',
    title12009: 'Administrative Sanctions',
    sections: [
      { sec9184: '66', title9184: 'Jurisdiction', sec12009: '93', title12009: 'Jurisdiction', status: 'transposed' },
      { sec9184: '67', title9184: 'Civil Liability in Case of Conviction', sec12009: '96', title12009: 'Civil Liability in Case of Conviction', status: 'transposed' },
      { sec9184: '68', title9184: 'Liquidated Damages', sec12009: '97', title12009: 'Liquidated Damages', status: 'transposed' },
      { sec9184: '69', title9184: 'Imposition of Administrative Penalties', sec12009: '-', title12009: 'Transposed to Sec. 100 as grounds for blacklisting', status: 'transposed' },
      { sec9184: '70', title9184: 'Preventive Suspension', sec12009: '94', title12009: 'Preventive Suspension', status: 'transposed' },
      { sec9184: '71', title9184: 'Lifting of Suspension and Removal of Administrative Disabilities', sec12009: '95', title12009: 'Lifting of Suspension and Removal of Administrative Disabilities', status: 'transposed' },
      { sec9184: '-', title9184: '-', sec12009: '92', title12009: 'Administrative Liability of Public Officers', status: 'added' }
    ]
  },
  {
    article: 'XXI',
    title9184: '[NONE]',
    title12009: 'Suspension and Blacklisting',
    sections: [
      { sec9184: '-', title9184: '-', sec12009: '98', title12009: 'Administrative Liability of Private Entities', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '99', title12009: 'Imposition of Suspension', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '100', title12009: 'Imposition of Blacklisting', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '101', title12009: 'Imposition of Blacklisting with Perpetual Disqualification', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '102', title12009: 'Suspension Pending', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '103', title12009: 'Status of Suspended or Blacklisted Person', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '104', title12009: 'Initiation of Action', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '105', title12009: 'Extent of Administrative Sanctions', status: 'added' }
    ]
  },
  {
    article: 'XXIV',
    title9184: 'Legal Assistance and Indemnification of BAC Members',
    title12009: 'Legal Assistance and Indemnification Package for BAC Members and Support Staff',
    sections: [
      { sec9184: '72', title9184: 'Private Legal Assistance', sec12009: '106', title12009: 'Private Legal Assistance', status: 'modified' },
      { sec9184: '73', title9184: 'Indemnification Package', sec12009: '107', title12009: 'Indemnification Package', status: 'transposed' }
    ]
  },
  {
    article: 'XXV',
    title9184: 'Final Provisions',
    title12009: 'Final Provisions',
    sections: [
      { sec9184: '74', title9184: 'Oversight Committee', sec12009: '108', title12009: 'Appropriations', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '109', title12009: 'Whole-of-Government Approach', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '111', title12009: 'Mandatory Review', status: 'added' },
      { sec9184: '75', title9184: 'Implementing Rules and Regulations', sec12009: '112', title12009: 'Implementing Rules and Regulations', status: 'modified' },
      { sec9184: '-', title9184: '-', sec12009: '113', title12009: 'Transitory Provision', status: 'added' },
      { sec9184: '-', title9184: '-', sec12009: '114', title12009: 'Reportorial Requirements', status: 'added' },
      { sec9184: '76', title9184: 'Repealing Clause', sec12009: '115', title12009: 'Repealing Clause', status: 'modified' },
      { sec9184: '77', title9184: 'Separability Clause', sec12009: '116', title12009: 'Separability Clause', status: 'modified' },
      { sec9184: '78', title9184: 'Effectivity Clause', sec12009: '117', title12009: 'Effectivity', status: 'modified' }
    ]
  }
];

export default function ComparativeMatrixPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [comparativeData, setComparativeData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch comparative matrix data on component mount
  useEffect(() => {
    const fetchComparativeData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/comparative-matrix`);
        if (!response.ok) {
          throw new Error('Failed to fetch comparative matrix data');
        }
        const data = await response.json();
        setComparativeData(data);
      } catch (error) {
        console.error('Error fetching comparative data:', error);
        toast.error('Failed to load comparative matrix data');
        setComparativeData(COMPARATIVE_DATA_BACKUP);
      } finally {
        setLoading(false);
      }
    };

    fetchComparativeData();
  }, []);

  const filteredData = comparativeData.map(article => ({
    ...article,
    sections: article.sections.filter(section => {
      const matchesSearch = 
        section.title9184.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.title12009.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || section.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
  })).filter(article => article.sections.length > 0);

  // Count sections by status
  const statusCounts = comparativeData.reduce((acc, article) => {
    article.sections.forEach(section => {
      acc[section.status] = (acc[section.status] || 0) + 1;
    });
    return acc;
  }, {});

  const getStatusBadge = (status) => {
    const styles = {
      retained: 'bg-blue-100 text-blue-800 border-blue-300',
      modified: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      added: 'bg-green-100 text-green-800 border-green-300',
      deleted: 'bg-red-100 text-red-800 border-red-300',
      renumbered: 'bg-purple-100 text-purple-800 border-purple-300',
      transposed: 'bg-orange-100 text-orange-800 border-orange-300',
      restructured: 'bg-indigo-100 text-indigo-800 border-indigo-300'
    };

    const icons = {
      retained: '=',
      modified: '~',
      added: <Plus className="w-3 h-3" />,
      deleted: <Minus className="w-3 h-3" />,
      renumbered: <ArrowRight className="w-3 h-3" />,
      transposed: '⤴',
      restructured: '⟲'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/library')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
            <p className="text-slate-600 dark:text-slate-400 text-lg">Loading comparative matrix data...</p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/library')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Section Title Matrix
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-4">Side-by-side comparison: RA 9184 vs RA 12009</p>
          
          {/* Legend - Compact */}
          <Card className="bg-white/80 dark:bg-slate-800/80 backdrop-blur dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-base dark:text-slate-100">Legend</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pb-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 border-b dark:border-slate-700">Category</th>
                      <th className="px-3 py-1.5 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 border-b dark:border-slate-700">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('retained')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.retained || 0}</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('modified')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.modified || 0}</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('added')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.added || 0}</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('deleted')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.deleted || 0}</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('renumbered')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.renumbered || 0}</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="px-3 py-1.5">{getStatusBadge('transposed')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.transposed || 0}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-1.5">{getStatusBadge('restructured')}</td>
                      <td className="px-3 py-1.5 text-center text-xs font-medium text-slate-600 dark:text-slate-400">{statusCounts.restructured || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <Input
            placeholder="Search sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            data-testid="search-matrix"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200" data-testid="status-filter">
              <SelectValue className="dark:text-slate-200" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="all" className="dark:text-slate-200">All Status</SelectItem>
              <SelectItem value="retained" className="dark:text-slate-200">Retained</SelectItem>
              <SelectItem value="modified" className="dark:text-slate-200">Modified</SelectItem>
              <SelectItem value="added" className="dark:text-slate-200">Added</SelectItem>
              <SelectItem value="deleted" className="dark:text-slate-200">Deleted</SelectItem>
              <SelectItem value="renumbered" className="dark:text-slate-200">Renumbered</SelectItem>
              <SelectItem value="transposed" className="dark:text-slate-200">Transposed</SelectItem>
              <SelectItem value="restructured" className="dark:text-slate-200">Restructured</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Comparison Table */}
        <div className="space-y-6">
          {filteredData.map((article, idx) => (
            <Card key={idx} className="overflow-hidden dark:bg-slate-800 dark:border-slate-700" data-testid={`article-comparison-${article.article}`}>
              <CardHeader className="bg-emerald-50 dark:bg-emerald-900/30 border-b dark:border-slate-700">
                <CardTitle className="text-2xl text-emerald-800 dark:text-emerald-300">
                  Article {article.article}
                </CardTitle>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">RA 9184</p>
                    <p className="text-base font-medium dark:text-slate-200">{article.title9184}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">RA 12009</p>
                    <p className="text-base font-medium dark:text-slate-200">{article.title12009}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Mobile scroll hint */}
                <p className="text-xs text-slate-500 dark:text-slate-400 px-4 pt-3 md:hidden flex items-center gap-1">
                  <Lightbulb className="w-3 h-3 flex-shrink-0" />
                  Scroll horizontally to see all columns →
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-slate-100 dark:bg-slate-900 border-b dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300 w-24">RA 9184 Sec</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300 w-[35%]">RA 9184 Title</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 dark:text-slate-300 w-36">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300 w-24">RA 12009 Sec</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-300 w-[35%]">RA 12009 Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.sections.map((section, sIdx) => (
                        <tr key={sIdx} className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{section.sec9184}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{section.title9184}</td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(section.status)}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{section.sec12009}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{section.title12009}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
